// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Sync + Lese-/Schreibpfad: PocketBase ⇄ IndexedDB ⇄ Outbox (Muster: Kraftbogen)
import { pb } from "./pb"
import {
  getAll, getAllSofort, put, deleteFrom, replaceAll, getMeta, setMeta,
  getOutbox, putOutbox, deleteOutbox,
} from "./idb"
import { flushOutbox } from "./outbox"
import type { Plan, PlanDay, PlanDayItem, Exercise, ExerciseNote, WorkoutEntry, EntryPayload, Feel, SetEntry } from "./types"

function ownerId(): string {
  return pb.authStore.record?.id ?? ""
}

// Sync-Bremse: kurzes Tab-Hin-und-Her lädt nicht jedes Mal alles neu
const SYNC_MIN_ABSTAND_MS = 5 * 60_000
// Übungskatalog (~850 KB, 1.353 Stück) ist quasi statisch: nur neu laden,
// wenn sich die Anzahl ändert oder der Stand älter als ein Tag ist
const KATALOG_FRISCHE_MS = 24 * 60 * 60_000

/** Katalog + eigene Einträge vom Server in die lokalen Caches ziehen.
    Standard: sofort (Login, nach Plan-Änderungen). Nur der Tab-Wechsel-
    Trigger übergibt `erzwingen=false` und respektiert die Sync-Bremse. */
export async function syncFromPocketBase(erzwingen = true): Promise<void> {
  if (!pb.authStore.isValid) return
  try {
    if (!erzwingen) {
      const letzter = await getMeta("lastSync")
      if (typeof letzter === "number" && Date.now() - letzter < SYNC_MIN_ABSTAND_MS) return
    }

    // Erst fragen, ob die Anmeldung überhaupt noch gilt. Ein abgelaufener oder
    // zurückgezogener Ausweis („auf allen Geräten abmelden") sieht von außen
    // gültig aus — der Server behandelt ihn aber wie einen Fremden und liefert
    // brav LEERE Listen. Ohne diese Frage hätte der Abgleich danach den
    // gesamten Offline-Vorrat mit Leere überschrieben: kein Plan mehr im
    // Studio, keine Übungen, nichts. (Beim Testen selbst hineingelaufen.)
    try {
      await pb.collection("users").authRefresh()
    } catch (e) {
      const status = (e as { status?: number }).status
      if (status === 401 || status === 403 || status === 404) {
        pb.authStore.clear() // die App zeigt dann die Anmeldung; lokale Daten bleiben
      }
      return
    }

    // Billige Vorab-Frage (1 Datensatz) statt 850 KB Katalog bei jedem Sync.
    // Sortiert nach letzter Änderung: so fällt auch auf, wenn der Admin ein
    // Video an einer bestehenden Übung hinterlegt (Anzahl bleibt gleich).
    const kopf = await pb.collection("exercises").getList<Exercise & { updated?: string }>(1, 1, { sort: "-updated" })
    const geladen = await getMeta("exercisesGeladen")
    const anzahl = await getMeta("exercisesAnzahl")
    const stand = await getMeta("exercisesStand")
    const neuesterStand = kopf.items[0]?.updated ?? ""
    const katalogNeu = anzahl !== kopf.totalItems
      || (neuesterStand !== "" && neuesterStand !== stand)
      || typeof geladen !== "number" || Date.now() - geladen > KATALOG_FRISCHE_MS

    const [plans, days, items, entries, notes, exercises] = await Promise.all([
      pb.collection("plans").getFullList<Plan>({ batch: 1000 }),
      pb.collection("plan_days").getFullList<PlanDay>({ batch: 1000 }),
      pb.collection("plan_day_items").getFullList<PlanDayItem>({ batch: 2000 }),
      pb.collection("workout_entries").getFullList<WorkoutEntry>({ batch: 2000 }),
      // Eigene Zettel sind winzig und hängen NICHT an der Katalog-Bremse —
      // sonst sähe man sein neues Video auf dem zweiten Gerät bis zu einen Tag nicht
      pb.collection("exercise_notes").getFullList<ExerciseNote>({ batch: 1000 }),
      katalogNeu ? pb.collection("exercises").getFullList<Exercise>({ batch: 2000 }) : Promise.resolve(null),
    ])
    await Promise.all([
      replaceAll("plans", plans),
      replaceAll("planDays", days),
      replaceAll("planDayItems", items),
      exercises ? replaceAll("exercises", exercises) : Promise.resolve(),
      replaceAll("exerciseNotes", notes),
      replaceAll("entries", entries.map((e) => ({ ...e, pending: false }))),
    ])
    if (exercises) {
      await setMeta("exercisesAnzahl", kopf.totalItems)
      await setMeta("exercisesStand", neuesterStand)
      await setMeta("exercisesGeladen", Date.now())
    }
    await setMeta("lastSync", Date.now())
    window.dispatchEvent(new CustomEvent("fithub:synced"))
  } catch {
    // offline oder Server nicht erreichbar → lokale Caches bleiben gültig
  }
}

/** Effekt-Helfer: lädt sofort aus dem Cache und erneut nach jedem Server-Abgleich. */
export function onSynced(load: () => void): () => void {
  window.addEventListener("fithub:synced", load)
  return () => window.removeEventListener("fithub:synced", load)
}

// ── Katalog-Lesepfade (immer aus IndexedDB — offlinefähig) ──

export const getPlans = () => getAll("plans")
export const getPlanDays = () => getAll("planDays")
export const getPlanDayItems = () => getAll("planDayItems")
export const getExerciseNotes = () => getAll("exerciseNotes")

/** Übungskatalog mit dem eigenen Zettel obendrauf: hinterlegtes Video und
 *  Notiz überlagern den gemeinsamen Katalog-Eintrag. Alle Seiten holen ihre
 *  Übungen hier — dadurch wirkt der Zettel überall, ohne Extra-Code. */
export async function getExercises(): Promise<Exercise[]> {
  const [katalog, notes] = await Promise.all([getAll("exercises"), getAll("exerciseNotes")])
  return mitZetteln(katalog, notes)
}

function mitZetteln(katalog: Exercise[], notes: ExerciseNote[]): Exercise[] {
  if (!notes.length) return katalog
  const byExercise = new Map(notes.map((n) => [n.exercise, n]))
  return katalog.map((e) => {
    const n = byExercise.get(e.id)
    if (!n) return e
    return {
      ...e,
      video_url: n.video_url || e.video_url,
      execution_text: n.note || e.execution_text,
      eigenerZettel: Boolean(n.video_url || n.note),
    }
  })
}

/** Der zuletzt gelesene Stand — synchron, also schon beim allerersten Bild da.
 *  Damit baut eine Seite beim Tab-Wechsel sofort fertig auf, statt leer zu
 *  blitzen; die frischen Daten aus IndexedDB kommen Millisekunden später.
 *  null heißt: in dieser Sitzung noch nie gelesen (dann lohnt kein Aufbau).
 *  ponytail: `entries` kennt die wartenden Outbox-Einträge nicht — die sind
 *  einen Wimpernschlag später da, das sieht niemand. */
export const sofort = {
  plans: () => getAllSofort("plans"),
  planDays: () => getAllSofort("planDays"),
  planDayItems: () => getAllSofort("planDayItems"),
  entries: (): WorkoutEntry[] | null => {
    const e = getAllSofort("entries")
    return e && [...e].sort((a, b) => a.date.localeCompare(b.date))
  },
  exercises: (): Exercise[] | null => {
    const katalog = getAllSofort("exercises")
    return katalog && mitZetteln(katalog, getAllSofort("exerciseNotes") ?? [])
  },
}

/** Tage des Plans, der gerade gilt — ohne ausdrückliche Wahl die erste Vorlage. */
export function planTage(plans: Plan[] | null, days: PlanDay[] | null): PlanDay[] {
  if (!plans || !days) return []
  const pid = activePlanId() || ((plans.find((p) => !p.owner) ?? plans[0])?.id ?? "")
  return days.filter((d) => d.plan === pid).sort((a, b) => a.sort - b.sort)
}

/** Übungen als Nachschlagwerk nach id — Eintragen, Plan und Training brauchen es. */
export const nachId = (exs: Exercise[] | null): Map<string, Exercise> =>
  new Map((exs ?? []).map((x) => [x.id, x]))

/** Aktiver Plan des angemeldeten Users (users.active_plan). */
export function activePlanId(): string {
  return String(pb.authStore.record?.active_plan ?? "")
}

// ── Einträge: synchronisierte + wartende zusammen lesen ──

function outboxToEntry(clientId: string, data: EntryPayload): WorkoutEntry {
  return { ...data, id: `pending-${clientId}`, pending: true }
}

export async function getAllEntries(): Promise<WorkoutEntry[]> {
  const [synced, queued] = await Promise.all([getAll("entries"), getOutbox()])
  const all = [...synced, ...queued.map((q) => outboxToEntry(q.clientId, q.data))]
  return all.sort((a, b) => a.date.localeCompare(b.date))
}

// ── Schreiben: immer über die Outbox (offline-first) ──

export interface NewEntry {
  date: string
  plan_day: string
  day_code: string
  exercise: string
  feel: Feel
  note: string
  sets: SetEntry[]
}

/** Eintrag speichern: in die Outbox, dann Best-Effort-Flush. Gibt den lokalen Eintrag zurück. */
export async function saveEntry(input: NewEntry): Promise<WorkoutEntry> {
  const clientId = crypto.randomUUID()
  const data: EntryPayload = { ...input, owner: ownerId(), client_id: clientId }
  await putOutbox({ clientId, data })
  void flushOutbox()
  return outboxToEntry(clientId, data)
}

/** Eintrag ändern. Wartende (pending) werden lokal umgeschrieben; gesendete direkt am Server (braucht Netz). */
export async function updateEntry(entry: WorkoutEntry, changes: Partial<NewEntry>): Promise<void> {
  if (entry.pending) {
    const clientId = entry.client_id
    const queued = (await getOutbox()).find((q) => q.clientId === clientId)
    if (queued) {
      await putOutbox({ clientId, data: { ...queued.data, ...changes } })
      void flushOutbox()
      return
    }
  }
  const updated = await pb.collection("workout_entries").update<WorkoutEntry>(entry.id, changes)
  await put("entries", { ...updated, pending: false })
}

/** Eintrag löschen. Wartende aus der Outbox nehmen; gesendete am Server (braucht Netz). */
export async function deleteEntry(entry: WorkoutEntry): Promise<void> {
  if (entry.pending) {
    await deleteOutbox(entry.client_id)
    return
  }
  await pb.collection("workout_entries").delete(entry.id)
  await deleteFrom("entries", entry.id)
}

/** Den kompletten eigenen Trainingsverlauf löschen: alle Einträge, die noch
 *  wartenden aus der Outbox und die Feed-Meldungen dieser Trainings — die sind
 *  derselbe Vorgang, nur öffentlich, und dürfen nicht überleben, wenn der
 *  Verlauf leer ist. Pläne, Körpergewicht und das Konto bleiben unangetastet.
 *  Braucht Netz. Gibt zurück, wie viele Einträge weg sind. */
export async function loescheVerlauf(): Promise<number> {
  // Die Leseregel liefert ohnehin nur die eigenen — kein Filter nötig.
  const eintraege = await pb.collection("workout_entries").getFullList<WorkoutEntry>({ batch: 500 })
  // ponytail: nacheinander. Bei ein paar hundert Einträgen dauert das kurz,
  // kann aber die Rate-Bremse nicht auslösen. Stapel-API erst, wenn es stört.
  for (const e of eintraege) await pb.collection("workout_entries").delete(e.id)
  const posts = await pb.collection("feed_posts")
    .getFullList<{ id: string }>({ filter: `owner = "${ownerId()}"`, batch: 500 })
    .catch(() => [])
  for (const p of posts) await pb.collection("feed_posts").delete(p.id).catch(() => {})
  // Wartende zuerst aus der Warteschlange nehmen, sonst schiebt der nächste
  // Abgleich sie brav wieder hoch und der Verlauf füllt sich von selbst.
  for (const q of await getOutbox()) await deleteOutbox(q.clientId)
  await replaceAll("entries", [])
  window.dispatchEvent(new CustomEvent("fithub:synced"))
  return eintraege.length
}
